/* eslint-disable i18next/no-literal-string */
import * as React from "react";
import { cn } from "~/lib/utils";
import { colors } from "~/components/landing/colors";
import { LandingPageHeaderH2 } from "~/components/landing/LandingText";
import { Picture } from "~/components/ui/picture";

/**
 * Ported from bouncieweb's `LandingPageComponents/BlogLinks.js`.
 *
 * Heading + centered intro paragraph + a 3-up row of blog cards (stacked on
 * mobile, row from md). Each card is an external link to bouncie.com/blog, so
 * the original `next/link` becomes a plain <a target="_blank"> here.
 *
 * The original's `WidthConstraintContainer extraWide` is `wrap-legacy-wide`.
 *
 * Original breakpoint map (theme/mediaQueries): md 768px -> md:  xlg 1280px -> xl:
 */

export type LandingBlogLink = {
  href: string;
  image: string;
  imageAlt: string;
  label: React.ReactNode;
  title: React.ReactNode;
};

export type LandingBlogLinksData = {
  heading: React.ReactNode;
  paragraph: React.ReactNode;
  blogs: readonly LandingBlogLink[];
};

export default function BlogLinks({
  blogLinksData,
}: {
  blogLinksData: LandingBlogLinksData;
}) {
  return (
    // Wrapper
    <div className="pt-16 pb-24">
      {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
      <div className="wrap-legacy-wide">
        <LandingPageHeaderH2 className="mt-0">
          {blogLinksData.heading}
        </LandingPageHeaderH2>

        <div className="flex justify-center">
          {/* CenteredParagraph */}
          <p className="mb-8 max-w-[1006px] text-center">
            {blogLinksData.paragraph}
          </p>
        </div>

        {/* Blogs */}
        <div className="flex flex-col gap-8 md:flex-row md:items-stretch">
          {blogLinksData.blogs.map((blog, index) => (
            // BlogLink
            <a data-sid={`blog-link:${blog.href}`}
              key={index}
              href={blog.href}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "overflow-hidden rounded-[8px] border border-solid border-[#D3D3D3] bg-white text-left",
                "shadow-[0px_0px_10px_rgba(0,0,0,0.15)] transition-[background] duration-100",
                "hover:bg-[#F2F5F9] hover:no-underline",
                "md:flex-[0_0_calc(33%-1.1rem)]",
              )}
            >
              {/* StyledImage */}
              <Picture
                src={blog.image}
                alt={blog.imageAlt}
                className="h-fit w-full max-w-none"
              />
              {/* TextSection */}
              <div
                className="px-6 pt-2 pb-6"
                style={{ color: colors.black }}
              >
                {/* Label */}
                <div
                  className="mb-2 text-[16px] font-bold"
                  style={{ color: colors.blueDark7 }}
                >
                  {blog.label}
                </div>
                {/* Title */}
                <div className="text-[20px] font-bold">{blog.title}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
